import * as PIXI from 'pixi.js';
import type { TimeRange } from '../';
import {
    assertBoolean,
    assertNumberBetween,
    assertObject,
    assertString,
    assertUuid,
} from '../validators';
import type {
    DestroyOptions,
    FetchResources,
    LifecycleAware,
    MediaSize,
    Resolution,
    SceneCategory,
} from '../types';

import { AspectRatio } from '../aspectRatioConfig';
import { addAssetToContainer } from '../libs/buildChildrenForScene';
import { SequentialTimeline } from '../libs/SequentialTimeline';
import type { TickerTimeline } from '../libs/TickerTimeline';
import { Config } from '../video-data/config';
import type { TextDirection, SeekMode } from '../enums';
import { BufferingMode } from '../enums';
import { BufferingAbortedError } from '../errors';
import { hasOwnProperty } from '../common';
import uuid from '../video-data/utils/uuid';
import { AudioAsset } from '../audio/AudioAsset';
import type { PlaybackRegion } from './PlaybackRegions';
import { ErrorReporter } from '../libs/error-reporter';
import type { CanvasReusableTexturesManager } from '../canvasReusableTexuresManager';
import { comparer, observable, reaction, runInAction } from './reactivity';
import type { AssetNetworkManager } from './AssetNetworkManager';
import { Scene } from './scene/Scene';
import type { AddSceneSettings } from './scene/types';
import type { SerializableMixinSettings } from './serializableMixin';
import type { SceneGroupManager } from './SceneGroupManager';
import { BaseGroup } from './BaseGroup';
import { TimeAwareBase } from './TimeAwareBase';
import type {
    Asset,
    AssetType,
    BaseAsset,
    GetAssetType,
    GetAssetsOptions,
} from './asset/BaseAsset';
import { CaptionsManager } from './captions/CaptionsManager';
import type { UpdateFlowMixinSettings } from './updateFlowMixin';
import { TimeAwareMediaAsset } from './asset/TimeAwareMediaAsset';
import { Transcript } from './Transcript';
import {
    syncTranscriptData,
    voicedrivenPlaybackRegionsFor,
} from './asset/FancyCaptionAsset/asset/FancyCaptionAsset';
import { CompositingAsset } from './CompositingAsset';
import { _isHelperDisplay } from './helper';

/** The group-level asset a scene's media asset mirrors when it is linked to the group. */
type LinkedMediaSource = TimeAwareMediaAsset | CompositingAsset;

/** A scene-level media asset that mirrors the group-level one. */
type LinkedMediaAsset = GetAssetType<typeof Config.MEDIA_ASSET_TYPE>;

/** An asset whose playback is driven by an audio waveform. */
type WaveformAsset =
    | GetAssetType<typeof Config.LOTTIE_ASSET_TYPE>
    | GetAssetType<typeof Config.ICON_ASSET_TYPE>;

export type SceneGroupSettings = {
    sceneGroupManager: SceneGroupManager;
    timeline: TickerTimeline;
    fetchResources: FetchResources;
    preserveAssetIds?: boolean;
    enableHelpers?: boolean;
    autoDraw?: boolean;
    disableCaptions?: boolean;
} & SerializableMixinSettings &
    UpdateFlowMixinSettings;

/**
 * SceneGroups contain a number of re-orderable scenes. The scenes in a SceneGroup must be a
 * continuous group of scenes in Luminary. Scenes cannot be moved to another scene group. They are
 * identified by their id (sceneGroup.id), which can be used to move or remove the scene group
 * through the SceneGroupManager. Manipulating scenes using the methods from this class will also
 * be reflected in Luminary (moving, removing, adding scenes will update the group and Luminary scenes).
 */
export class SceneGroup extends TimeAwareBase {
    override _classType = Config.SCENE_GROUP_TYPE;
    override _name = Config.SCENE_GROUP_TYPE;

    @observable accessor #scenes: Scene[] = [];
    @observable.ref accessor #mediaAsset: TimeAwareMediaAsset | null = null;
    @observable.ref accessor #voiceoverAsset: AudioAsset | null = null;
    readonly captions: CaptionsManager;
    readonly avatar: CompositingAsset;

    readonly #sceneGroupManager: SceneGroupManager;
    readonly #workspace: BaseGroup;
    readonly #scenesWorkspace: BaseGroup;
    readonly #assetsWorkspace: BaseGroup;
    readonly #helperScene = new PIXI.Container();
    readonly #globalTimeline: TickerTimeline;

    #aspectRatio: AspectRatio = AspectRatio.LANDSCAPE;
    #scenesById = new Map<string, Scene>();
    #preserveAssetIds: boolean;
    #enableHelpers: boolean;
    #musicVolumeOverride: number | null = null;
    #autoDraw = false;
    #originalTranscript: Transcript;
    #waveformAudioAsset: AudioAsset | null = null;
    #timeline = new SequentialTimeline({
        name: 'scene_group_timeline',
        duration: Config.DEFAULT_SCENE_DURATION,
    });

    #abortBuffering: (error: BufferingAbortedError) => void = () => {};
    // Returns true if any asset has changed since the last time we called animate
    #assetsChangedSinceLastAnimate: boolean = true;
    #lastAnimatedTime: number = -1;
    // Returns true if any scene has moved or resized since the last time we called animate
    #layoutChangedSinceLastAnimate: boolean = true;

    constructor(settings: SceneGroupSettings) {
        super(settings);
        this.#sceneGroupManager = settings.sceneGroupManager;
        this.#globalTimeline = settings.timeline;
        this.#preserveAssetIds = settings.preserveAssetIds!;
        this.#enableHelpers = settings.enableHelpers!;

        if (settings.hasOwnProperty('autoDraw')) {
            this.#autoDraw = settings.autoDraw!;
        }

        // Here we compose the following PIXI hierarchy
        // + workspace (gets rendered)
        // +-+ scenesWorkspace (contains our scenes)
        // +-+ assetsWorkspace (contains any scene group level assets)
        //   +-+ captionManager
        //   +-+ helperScene

        this.#workspace = new BaseGroup(this, {
            autoDraw: this.autoDraw,
            fetchResources: this.fetchResources,
            timeline: this.#globalTimeline,
            isFinalTopLevel: true,
            name: 'sceneGroupWorkspace',
            updateEventSource: this, // any child changes eventually get emitted through `this.on('childUpdated')`
            assetNetworkManager: this.root._assetNetworkManager,
            getAspectRatio: () => this.root.aspectRatio,
            reusableTextures: this.root._reusableTextures,
            getReferenceResolution: () => this.root.referenceResolution,
            getOutputResolution: () => this.root.outputResolution,
        });
        this.#workspace.visible = false;

        // scenes DO NOT bubble up their changes and are instead manually propagated
        // we do this so we can better control what gets updated -- see slide for more details
        this.#scenesWorkspace = new BaseGroup(this, {
            autoDraw: this.autoDraw,
            fetchResources: this.fetchResources,
            timeline: this.#globalTimeline,
            isFinalTopLevel: false,
            name: 'scenesWorkspace',
            assetNetworkManager: this.root._assetNetworkManager,
            getAspectRatio: () => this.root.aspectRatio,
            reusableTextures: this.root._reusableTextures,
            getReferenceResolution: () => this.root.referenceResolution,
            getOutputResolution: () => this.root.outputResolution,
        });

        // all updates pass through this.#workspace
        this.#assetsWorkspace = new BaseGroup(this, {
            autoDraw: this.autoDraw,
            fetchResources: this.fetchResources,
            timeline: this.#globalTimeline,
            isFinalTopLevel: false,
            updateParentOnRedraw: true,
            name: 'assetsWorkspace',
            assetNetworkManager: this.root._assetNetworkManager,
            getAspectRatio: () => this.root.aspectRatio,
            reusableTextures: this.root._reusableTextures,
            getReferenceResolution: () => this.root.referenceResolution,
            getOutputResolution: () => this.root.outputResolution,
        });

        this.#workspace.insertAsset(this.#scenesWorkspace);
        this.#workspace.insertAsset(this.#assetsWorkspace);

        let disableCaptions = false;
        if (settings.hasOwnProperty('disableCaptions')) {
            disableCaptions = settings.disableCaptions!;
        }

        // all updates pass through this.#assetsWorkspace which bubble up to this
        this.captions = new CaptionsManager(this, {
            autoDraw: this.autoDraw,
            fetchResources: this.fetchResources,
            timeline: this.#globalTimeline,
            name: 'captionManager',
            isFinalTopLevel: true,
            updateParentOnRedraw: true,
            enableBoundsWithoutAnimationForSubtree: true,
            disableCaptions,
            getScenesDisplayCaptions: () =>
                this.getActiveScenesAt(this.currentTime).every(
                    (scene) => scene.displayCaptions === true,
                ),
            assetNetworkManager: this.root._assetNetworkManager,
            getAspectRatio: () => this.root.aspectRatio,
            reusableTextures: this.root._reusableTextures,
            getReferenceResolution: () => this.root.referenceResolution,
            getOutputResolution: () => this.root.outputResolution,
        });
        this.#assetsWorkspace.insertAsset(this.captions);
        this.#assetsWorkspace._addPixiChild(this.#helperScene);

        this.avatar = new CompositingAsset(this, {
            isFinalTopLevel: true,
            fetchResources: this.fetchResources,
            timeline: this.#globalTimeline,
            autoDraw: this.autoDraw,
            name: 'AvatarAsset',
            updateLuminaryParentOnRedraw: true,
            assetNetworkManager: this.root._assetNetworkManager,
            getAspectRatio: () => this.root.aspectRatio,
            reusableTextures: this.root._reusableTextures,
            mediaSizeToUse: this.root.mediaSizeToUse,
            getReferenceResolution: () => this.root.referenceResolution,
            getOutputResolution: () => this.root.outputResolution,
        });

        this.#originalTranscript = new Transcript({
            parent: this,
            fetchResources: this.fetchResources,
            autoUpdate: this.autoUpdate,
        });

        // events
        this.#setupOnStart();
        this.#setupOnEnd();

        // this event gets emitted by any child change from:
        // - this.#workspace
        // - any slide which manually calls _emitChildUpdate() or has any of it's children update
        this.on('childUpdated', (name: string, sceneIndex: number, subsceneIndex: number) => {
            this.#assetsChangedSinceLastAnimate = true;

            // Propagate asset update flow changes to our base update flow manually.
            // The audio manager is dependent on scene properties (media asset tracks), such
            // as volume and duration. When we update these properties we don't
            // trigger the root Luminary update loop. This is mostly fine except for audio.
            // Changes to the media asset (e.g. volume) needs to propagate to the manager.
            if (!this.root.hasPendingRedraw) {
                this.root.atomicUpdate(() => {
                    this.root._updateAudioAnimationManager();
                });
            }

            // bubble up changed event to root
            this.root._emitChanged();

            // render all preview images for each scene for a scene group asset level change
            if (name === this.#workspace.name) {
                this.#scenes.forEach((scene) => {
                    scene._emitViewChanged();
                });

                // TODO(LUM-662): remove this rebuild once `SceneGroup.helpers` tracks the flags, colours and masks.
                if (this.#enableHelpers) {
                    SceneGroup.#updateHelpers(
                        this.#helperScene,
                        this.#assetsWorkspace._getAllBaseAssetsWithDisplayedHelpers(),
                    );
                }
            }
            // render the specified scene images for a scene level change
            else if (name === 'scene') {
                this.#scenes[sceneIndex]?._emitViewChanged(subsceneIndex);
            }
            // render nothing if nothing is specified
        });

        this.addReaction(
            reaction(
                () => ({
                    source: this.#getLinkedMediaAssetSource() ?? null,
                    linked: this.#getLinkedMediaAssets(),
                }),
                ({ source, linked }) =>
                    this.#batchRun(() => {
                        if (!source) {
                            return;
                        }
                        linked.forEach((media) => media.linkSourceTo(source));
                    }),
                {
                    equals: (a, b) => a.source === b.source && comparer.shallow(a.linked, b.linked),
                    name: 'SceneGroup.linkedMediaSource',
                },
            ),
        );

        this.addReaction(
            reaction(
                () => {
                    const source = this.#getLinkedMediaAssetSource() ?? null;
                    return {
                        source,
                        linked: this.#getLinkedMediaAssets(),
                        grading: SceneGroup.#gradingSnapshot(source ?? undefined),
                    };
                },
                ({ source, linked }) =>
                    this.#batchRun(() =>
                        SceneGroup.#applyLinkedMediaGrading(source ?? undefined, linked),
                    ),
                {
                    equals: (a, b) =>
                        a.source === b.source &&
                        comparer.shallow(a.linked, b.linked) &&
                        comparer.structural(a.grading, b.grading),
                    name: 'SceneGroup.linkedMediaGrading',
                },
            ),
        );

        this.addReaction(
            reaction(
                () => ({
                    captionAsset: this.captions.asset,
                    playbackRegions: this.#captionsPlaybackRegions(),
                }),
                ({ captionAsset, playbackRegions }) =>
                    this.#batchRun(() =>
                        SceneGroup.#updateCaptionsDuration(captionAsset, playbackRegions),
                    ),
                { equals: comparer.shallow, name: 'SceneGroup.captionsDuration' },
            ),
        );

        this.addReaction(
            reaction(
                () => ({ avatar: this.avatar, times: this.#avatarTimesFromVoiceover() }),
                ({ avatar, times }) =>
                    this.#batchRun(() => SceneGroup.#updateAvatarDuration(avatar, times)),
                {
                    equals: (a, b) =>
                        a.avatar === b.avatar && comparer.structural(a.times, b.times),
                    name: 'SceneGroup.avatarDuration',
                },
            ),
        );

        this.addReaction(
            reaction(
                () => ({
                    mp3Url: this.#voiceoverAsset?.sourceData?.mp3_url ?? null,
                    foreground: this.avatar.foreground,
                }),
                ({ mp3Url, foreground }, previous) =>
                    this.#batchRun(() =>
                        SceneGroup.#clearAvatarRenderUrlOnVoiceoverChange(
                            foreground,
                            mp3Url,
                            previous?.mp3Url ?? null,
                        ),
                    ),
                { equals: comparer.shallow, name: 'SceneGroup.avatarRenderUrl' },
            ),
        );

        this.addReaction(
            reaction(
                () => {
                    const captions = this.#fancyCaptionAssets();
                    return {
                        captions,
                        regions: captions.map(voicedrivenPlaybackRegionsFor),
                        transcript: this.#originalTranscript,
                        transcriptData: this.#originalTranscript.transcriptData,
                        managerCaptionEmpty: this.captions.transcript.empty,
                    };
                },
                ({ captions, transcript }) =>
                    this.#batchRun(() => this.#updateFancyCaptionTranscript(captions, transcript)),
                {
                    equals: (a, b) =>
                        comparer.shallow(a.captions, b.captions) &&
                        comparer.structural(a.regions, b.regions) &&
                        a.transcript === b.transcript &&
                        comparer.structural(a.transcriptData, b.transcriptData) &&
                        a.managerCaptionEmpty === b.managerCaptionEmpty,
                    name: 'SceneGroup.fancyCaptionTranscript',
                },
            ),
        );

        this.addReaction(
            reaction(
                () => ({
                    waveforms: this.#waveformAssets(),
                    voiceover: this.#voiceoverAsset,
                    media: this.#mediaAsset,
                    audioUrl: this.#mediaAsset?.sourceData?.audio_url ?? null,
                    volume: this.#mediaAsset?.volume ?? null,
                    playbackRegions: this.#mediaAsset?.playbackRegions ?? null,
                }),
                ({ waveforms, voiceover, media }) =>
                    this.#batchRun(() =>
                        this.#updateMediaAssetIfSceneGroupContainsWaveform(
                            waveforms,
                            voiceover,
                            media,
                        ),
                    ),
                {
                    equals: (a, b) =>
                        comparer.shallow(a.waveforms, b.waveforms) &&
                        a.voiceover === b.voiceover &&
                        a.media === b.media &&
                        a.audioUrl === b.audioUrl &&
                        a.volume === b.volume &&
                        comparer.structural(a.playbackRegions, b.playbackRegions),
                    name: 'SceneGroup.waveformAudio',
                },
            ),
        );

        // TODO(LUM-661): an asset's `#displaysHelpers` is not observable, so this covers which assets exist
        // rather than which of them display helpers; the `childUpdated` handler above covers the rest, and
        // helper positions, which come from rendered bounds.
        if (this.#enableHelpers) {
            this.addReaction(
                reaction(
                    () => this.#assetsWorkspace._getAllBaseAssetsWithDisplayedHelpers(),
                    (assetsWithHelpers) =>
                        this.#batchRun(() =>
                            SceneGroup.#updateHelpers(this.#helperScene, assetsWithHelpers),
                        ),
                    { equals: comparer.shallow, name: 'SceneGroup.helpers' },
                ),
            );
        }

        // TODO(LUM-661): a derivation whose whole effect is to flag an imperative caller. `seek()` is
        // driven by the frame loop rather than by the state it draws, so a layout change cannot reach
        // it as a value. Once draw derives, this reaction and the flag it sets both go.
        //
        // This group's own placement leads, because the reflow it has to catch is the video moving
        // underneath it: a transition appearing anywhere before it shifts it and everything after,
        // leaving what it drew stale while its own time never changed. Its scenes follow, for a
        // reflow inside it. The child itself is in the value so that a reorder of equal-duration
        // scenes is not read as no change. Anything nested reaches `seek` through `childUpdated`.
        this.addReaction(
            reaction(
                () => [
                    this.#timeline._relativeStartTime,
                    this.#timeline.duration,
                    ...this.#timeline._children.flatMap((child) => [
                        child,
                        child._relativeStartTime,
                        child.duration,
                    ]),
                ],
                (_) => {
                    this.#layoutChangedSinceLastAnimate = true;
                },
                { equals: comparer.shallow, name: 'SceneGroup.sceneLayout' },
            ),
        );
    }

    /**
     * Play the scene group. If no time range is given, then plays from the start to the end of the group.
     * Time is relative to the scene group. { startTime: 0, endTime: sceneGroup.duration } plays the entire scene group.
     * If one wants to play after pausing, pass { startTime: sceneGroup.currentTime }.
     */
    async play(playRange?: Partial<TimeRange>) {
        const adjustPlayRangeToGlobal = (playRange?: Partial<TimeRange>) => {
            const startTime =
                typeof playRange?.startTime === 'number'
                    ? playRange.startTime + this.startTime
                    : this.startTime;
            const endTime =
                typeof playRange?.endTime === 'number'
                    ? playRange.endTime + this.startTime
                    : this.endTime;

            return { startTime, endTime };
        };

        await this.root._play(adjustPlayRangeToGlobal(playRange), this);
    }

    /**
     * Get all assets in our hierarchy
     */
    getAssets(options: GetAssetsOptions = { deep: true }): Asset[] {
        // we want to filter out the canvas caption asset here as it is part of the scene group and serialized as such
        const children = this.#workspace
            .getAssets(options)
            .filter((asset) => !Config.isCaptionAsset(asset));
        if (this.mediaAsset) {
            children.push(this.mediaAsset);
        }
        children.push(...this.avatar.getAssets(options));
        return children;
    }

    /**
     * Get all assets of the given classType in our hierarchy.
     * @param classType - the classType of the component we're searching for
     */
    getAssetsByType<T extends AssetType>(
        classType: T,
        options?: GetAssetsOptions,
    ): GetAssetType<T>[];
    getAssetsByType(classType: AssetType, options?: GetAssetsOptions) {
        const children = this.#workspace.getAssetsByType(classType, options);
        if (classType === Config.MEDIA_ASSET_TYPE && this.mediaAsset) {
            children.push(this.mediaAsset);
        }
        const assetsByType = this.avatar.getAssetsByType(classType, options);
        if (assetsByType) {
            children.push(...assetsByType);
        }
        return children;
    }

    getAssetById(id: string, options?: GetAssetsOptions): Asset | undefined {
        if (this.mediaAsset?.id === id) {
            return this.mediaAsset;
        }
        if (this.avatar?.foreground?.id === id) {
            return this.avatar.foreground;
        }
        if (this.avatar?.background?.id === id) {
            return this.avatar.background;
        }
        return this.#workspace.getAssetById(id, options);
    }

    generateNewAssetIds() {
        if (this.mediaAsset) {
            this.mediaAsset.generateNewId();
        }
        this.avatar.generateNewAssetIds();
        this.#scenes.forEach((scene) => scene.generateNewAssetIds());
    }

    /**
     * @internal
     */
    _updateAspectRatio(value: AspectRatio) {
        if (this.#aspectRatio === value) {
            return;
        }
        this.#aspectRatio = value;
        this.captions._aspectRatio = value;
        this.#scenes.forEach((scene) => scene._updateAspectRatio(value));
    }

    get hasPendingRedraw() {
        const assets = [this.captions, this.avatar];
        return (
            assets.some((asset) => asset.requiresRedraw) ||
            this.#scenes.some((scene) => scene.hasPendingRedraw)
        );
    }

    /**
     * The playback regions the captions manager should run on, or null when there is no voice driven
     * asset to take them from yet.
     */
    #captionsPlaybackRegions(): Readonly<Readonly<PlaybackRegion>[]> | null {
        if (this.mediaAsset) {
            if (!this.mediaAsset.isSourceDataSet) {
                return null;
            }
            return this.mediaAsset.playbackRegions;
        }
        if (this.voiceoverAsset) {
            return this.voiceoverAsset.playbackRegions;
        }
        return null;
    }

    static #updateCaptionsDuration(
        captionAsset: CaptionsManager['asset'],
        playbackRegions: Readonly<Readonly<PlaybackRegion>[]> | null,
    ) {
        // The duration of the captions manager is dependent on the duration of the voice driven asset.
        // Each time the asset changes we need to update the captions manager duration.
        if (!playbackRegions) {
            return;
        }
        captionAsset.playbackRegions = playbackRegions;
    }

    /**
     * The times the avatar should run over, or null while the voiceover it is generated from has yet
     * to load. The background asset is left alone: it is not generated from the voiceover asset, only
     * the foreground -- the avatar actor itself -- is.
     */
    #avatarTimesFromVoiceover(): { startTime: number; endTime: number } | null {
        if (!this.voiceoverAsset || !this.avatar.foreground) {
            return null;
        }
        if (!this.voiceoverAsset.isLoaded) {
            return null;
        }
        return {
            startTime: this.voiceoverAsset.startTime,
            endTime: this.voiceoverAsset.endTime,
        };
    }

    static #updateAvatarDuration(
        avatar: CompositingAsset,
        times: { startTime: number; endTime: number } | null,
    ) {
        if (!times) {
            return;
        }
        avatar.startTime = times.startTime;
        avatar.endTime = times.endTime;
    }

    _updateDurationBasedOnMediaAsset = () => {
        // We should make sure that duration of scene group is always less than or equal to the duration of the media asset.
        // When we apply media asset to a scene group, we don't know the duration of the media asset
        // until it loads.
        // different browsers calculate the duration of the media asset differently.
        // This is why we need to adjust the duration in particular runtime like in chrome, safari or nodejs.
        // The duration of scene group with media asset is dependent on the duration of the media asset.
        // Each time the asset changes we need to update the scene group duration.
        // We can do that by adjusting the duration of the last subscene.

        if (!this.mediaAsset) {
            return;
        }

        // wait for the media asset to load
        if (this.mediaAsset.duration === 0) {
            return;
        }

        if (this.mediaAsset.duration >= this.duration) {
            return;
        }

        let delta = this.duration - this.mediaAsset.duration;
        if (delta > 500) {
            const errorMessage = `The duration of the scene group is greater than the duration of the media asset. It usually means that you need to wrap sequence of operations in the 'batchRun' call`;
            console.error(errorMessage);
            ErrorReporter.capture(errorMessage, {
                level: 'error',
                extra: {
                    delta,
                    sceneGroupDuration: this.duration,
                },
            });
            return;
        }

        while (delta > 0) {
            const lastScene = this.lastScene();
            if (!lastScene) {
                return;
            }
            const lastSubscene = lastScene.subscenes[lastScene.subscenes.length - 1];
            if (!lastSubscene) {
                return;
            }
            if (lastSubscene.duration <= delta) {
                // the delta is longer than the last subscene duration.
                // remove the whole subscene!
                delta -= lastSubscene.duration;
                lastScene.removeSubslide(lastSubscene.index);
                if (lastScene.subscenes.length === 0) {
                    this.removeScene(lastScene.id);
                }
            } else {
                // the subscene duration just needs to be changed by delta
                lastSubscene.duration -= delta;
                delta = 0;
            }
        }
    };

    static #removeHelpers(helperScene: PIXI.Container) {
        while (helperScene.children.length !== 0) {
            helperScene.children[0].destroy(true);
        }
    }

    /**
     * Not an action. The helper scene is a `PIXI.Container`, so its membership is not observable and no derivation
     * can see the rebuild — helpers are output, and nothing derives from them.
     */
    static #updateHelpers(helperScene: PIXI.Container, assetsWithHelpers: BaseAsset[]) {
        SceneGroup.#removeHelpers(helperScene);
        assetsWithHelpers.forEach((child) => {
            child.createHelpers().forEach((helper) => {
                helperScene.addChild(helper);
            });
        });
    }

    #drawHelpers() {
        this.#helperScene.children.filter(_isHelperDisplay).forEach((helper) => helper._draw());
    }

    /**
     * @internal
     */
    get _helperScene() {
        return this.#helperScene;
    }

    /**
     * Every asset in the group that runs off a waveform, in tree order.
     */
    #waveformAssets() {
        return this.getAssetsByType(Config.LOTTIE_ASSET_TYPE)
            .concat(this.getAssetsByType(Config.ICON_ASSET_TYPE))
            .filter((asset) => asset.playbackMode === Config.PLAYBACK_WAVEFORM);
    }

    #updateMediaAssetIfSceneGroupContainsWaveform(
        waveformAssets: WaveformAsset[],
        voiceoverAsset: AudioAsset | null,
        mediaAsset: TimeAwareMediaAsset | null,
    ) {
        if (waveformAssets.length === 0) {
            this.#waveformAudioAsset?.destroy();
            this.#waveformAudioAsset = null;
            return;
        }

        const audioAssetForWaveform = (() => {
            if (voiceoverAsset) {
                this.#waveformAudioAsset?.destroy();
                this.#waveformAudioAsset = null;
                return voiceoverAsset;
            }

            const audioUrl = mediaAsset?.sourceData.audio_url;
            if (!mediaAsset || !audioUrl) {
                this.#waveformAudioAsset?.destroy();
                this.#waveformAudioAsset = null;
                return undefined;
            }

            if (!this.#waveformAudioAsset) {
                this.#waveformAudioAsset = new AudioAsset({
                    fetchResources: this.fetchResources,
                    name: 'SceneGroupWaveformAudio',
                    autoUpdate: this.autoUpdate,
                    parent: this,
                    getAudioContext: () => this.root.audioContext,
                    getIsExternallyMuted: () => this.root.muted,
                });
                this.#waveformAudioAsset.muted = true;
            }
            this.#waveformAudioAsset.volume = mediaAsset.volume;
            this.#waveformAudioAsset.url = audioUrl;
            this.#waveformAudioAsset._playbackRegionsCustomLoad(
                mediaAsset.playbackRegions as PlaybackRegion[],
            );
            return this.#waveformAudioAsset;
        })();

        waveformAssets.forEach((waveform) => {
            waveform._waveformAudioAsset = audioAssetForWaveform;
        });
    }

    /**
     * Syncs the given fancy caption assets to `originalTranscript`. The captions manager's own asset
     * is only filled while it has no transcript: a fancy caption does not serialize its transcript, so
     * a loaded video (as in the render worker) has no other source of words for it, while a
     * transcript written to the manager directly, as the editor does for translations, must win.
     */
    #updateFancyCaptionTranscript(
        captionAssets: GetAssetType<typeof Config.FANCY_CAPTION_ASSET_TYPE>[],
        transcript: Transcript,
    ) {
        captionAssets
            .filter((asset) => asset !== this.captions.asset || asset.transcript.empty)
            .forEach((asset) => syncTranscriptData(asset, transcript));
    }

    /**
     * Returns the playback regions of the sceneGroup's media or voiceover asset.
     */
    get voicedrivenAssetPlaybackRegions() {
        if (this.mediaAsset) {
            return this.mediaAsset.playbackRegions;
        }
        if (this.voiceoverAsset) {
            return this.voiceoverAsset.playbackRegions;
        }
        return [];
    }

    get voicedrivenAssetDuration() {
        if (this.mediaAsset) {
            return this.mediaAsset.sourceDuration;
        }
        if (this.voiceoverAsset) {
            return this.voiceoverAsset.sourceDuration;
        }
        return 0;
    }

    /**
     * Returns true if the scene group has any audio.
     */
    get hasAudio() {
        if (this.mediaAsset?.hasAudio) {
            return true;
        }
        if (this.voiceoverAsset) {
            return true;
        }
        return this.#scenes.some((scene) => scene.hasAudio);
    }

    /**
     * Add a scene to the group (The settings are the same as Luminary.addScene)
     */
    addScene(settings: AddSceneSettings): Scene {
        const luminary = this.root;
        let id = settings?.id;

        // if not passed in an id, generate a new one
        if (id === undefined) {
            // first check if `data.id` is set
            if (hasOwnProperty(settings, 'data') && settings['data']!.id) {
                id = settings.data!.id as string;
            } else {
                id = uuid();
            }
        }
        if (this.#scenesById.get(id) !== undefined) {
            throw new Error('A scene id was specified, but a scene with this id already exists.');
        }

        assertUuid(id);

        if (!hasOwnProperty(settings, 'data')) {
            assertString(settings.sceneDesignId!);
        } else {
            assertObject(settings.data!);
        }

        const initAutoDraw = this.autoDraw;
        const initAutoUpdate = this.autoUpdate;
        this.autoDraw = false;
        this.autoUpdate = false;
        let scene: Scene;

        try {
            // create scene
            scene = new Scene({
                // since we're currently building a scene design on init, we have to ensure we don't try to process any
                // updates here since the scene isn't fully integrated into the system.
                id: id,
                sceneGroup: this,
                timeline: this.#globalTimeline,
                enableHelpers: this.#enableHelpers,
                autoUpdate: false,
                autoDraw: false,
                parent: this,
                fetchResources: this.fetchResources,
                getAspectRatio: () => this.root.aspectRatio,
                reusableTextures: this.root._reusableTextures,
                getReferenceResolution: () => luminary.referenceResolution,
            });
            scene._aspectRatio = luminary.aspectRatio;
            scene.textDirection = luminary.textDirection;

            // after applying the global properties to the scene, load the data of scene design data
            if (hasOwnProperty(settings, 'data')) {
                scene.load(settings.data!);
            } else {
                const sceneDesignData = luminary._getSceneDesignConfig(
                    settings.sceneDesignId!,
                    luminary.aspectRatio,
                );
                scene.load(sceneDesignData);
                if (!this.#preserveAssetIds) {
                    scene.generateNewAssetIds();
                }
            }

            this.#verifySceneCategoryCount(scene, Config.CATEGORY_OUTRO, 1);
            this.#verifySceneCategoryCount(scene, Config.CATEGORY_BRAND, 1);

            runInAction(() => {
                this.#scenes.push(scene);
                this.#scenesById.set(id, scene);
                this.#scenesWorkspace.insertAsset(scene.sceneWorkspace);
                // TODO - we have similar logic in index._updateTransition()
                // TODO - clean up with LU-2447
                //  for now have to move transition applied on first scene to the scene group
                if (this.#scenes.length === 1 && scene._timeline.offset !== 0) {
                    this.#timeline.offset = scene._timeline.offset;
                    scene._timeline.offset = 0;
                }
                this.#timeline.add(scene._timeline);
                // communicate state change to our consumers. Since we're not drawing any changes, we won't get any stateChanged
                // events from our children. Hence, we have to call it manually here.
                this.requiresUpdate = true;
            });
        } finally {
            // process any changes, if necessary, now that our scene is fully integrated
            this.autoUpdate = initAutoUpdate;
            this.autoDraw = initAutoDraw;
        }

        return scene;
    }

    #verifySceneCategoryCount(scene: Scene, category: SceneCategory, allowedCount = 1) {
        if (scene.sceneCategory !== category) {
            return;
        }
        const sceneCount = this.#scenes.length;
        if (sceneCount >= allowedCount) {
            scene.destroy(true);
            throw new Error(`Cannot add ${category} scene to existing scene group.`);
        }

        const currentSceneCount = this.#scenes.filter(
            (scene) => scene.sceneCategory === category,
        ).length;
        if (currentSceneCount >= allowedCount) {
            scene.destroy(true);
            throw new Error(`Cannot add more than one ${category} scene.`);
        }
    }

    /**
     * Add a brand new asset of type assetType to the scene
     */
    addAsset<T extends AssetType>(assetType: T, settings = {}): GetAssetType<T> {
        return addAssetToContainer(
            this.#assetsWorkspace as unknown as Parameters<typeof addAssetToContainer>[0],
            assetType,
            settings,
        );
    }

    // TODO: rename it to talkingHeadMediaAsset: https://lumen5.atlassian.net/browse/LU-2720
    get mediaAsset() {
        return this.#mediaAsset;
    }

    /**
     * Add a MediaAsset to this SceneGroup
     *
     * Note that you still need to set sourceData and volume
     * after calling adding the media asset.
     */
    addMediaAsset(id?: string) {
        if (this.#mediaAsset) {
            throw new Error('Scene group already has a media asset. Cannot add media');
        }
        if (this.avatar.foreground || this.avatar.background) {
            throw new Error('Scene group already has an avatar asset. Cannot add media');
        }
        if (this.#voiceoverAsset) {
            throw new Error('Scene group already has a voiceover asset. Cannot add media asset.');
        }

        const settings: {
            isFinalTopLevel: boolean;
            fetchResources: FetchResources;
            timeline: TickerTimeline;
            autoDraw: boolean;
            name: string;
            updateLuminaryParentOnRedraw: boolean;
            audioMode: string;
            assetNetworkManager: AssetNetworkManager;
            getAspectRatio: () => AspectRatio;
            seekWaitTime: number;
            reusableTextures: CanvasReusableTexturesManager;
            mediaSizeToUse: MediaSize;
            id?: string;
            getReferenceResolution: () => Resolution;
            getOutputResolution: () => Resolution;
        } = {
            isFinalTopLevel: true,
            fetchResources: this.fetchResources,
            timeline: this.#globalTimeline,
            autoDraw: this.autoDraw,
            name: 'SceneGroupMedia',
            updateLuminaryParentOnRedraw: true,
            audioMode: this.root.audioMode,
            assetNetworkManager: this.root._assetNetworkManager,
            seekWaitTime: this.root.settings.video?.seekWaitTime ?? 2000,
            mediaSizeToUse: this.root.mediaSizeToUse,
            getAspectRatio: () => this.root.aspectRatio,
            reusableTextures: this.root._reusableTextures,
            getReferenceResolution: () => this.root.referenceResolution,
            getOutputResolution: () => this.root.outputResolution,
            ...(id ? { id } : {}),
        };
        this.#mediaAsset = new TimeAwareMediaAsset(this, settings);

        // Register with Luminary asset index for O(1) lookups
        this.root._registerAsset(this.#mediaAsset);

        this.requiresUpdate = true;

        return this.#mediaAsset;
    }

    /**
     * Remove this group's MediaAsset
     */
    removeMediaAsset() {
        if (!this.#mediaAsset) {
            throw new Error('removeMediaAsset called while SceneGroup has no media');
        }
        const linkedAssets = this.#getLinkedMediaAssets();
        linkedAssets.forEach((media) => {
            media.clear();
        });
        this.#mediaAsset.destroy();
        this.#mediaAsset = null;
        this.requiresUpdate = true;
    }

    /**
     * @deprecated Use {@link avatar} instead. This is a temporary alias for backwards compatibility.
     */
    get avatarAsset(): TimeAwareMediaAsset | null {
        return this.avatar?.foreground ?? null;
    }

    /**
     * @deprecated Use avatar instead. This is a temporary alias for backwards compatibility.
     */
    addAvatarAsset(id?: string) {
        if (this.mediaAsset) {
            throw new Error('Scene group already has a media asset. Cannot add avatar asset.');
        }
        if (this.avatar?.foreground) {
            return this.avatar.foreground;
        }
        return this.avatar.addForeground(id);
    }

    get voiceoverAsset() {
        return this.#voiceoverAsset;
    }

    /**
     * Whether this scene group's timing is driven by speech — a talking head (`mediaAsset`) or a
     * voiceover (`voiceoverAsset`). Voice-driven timing is fixed by the audio.
     */
    get isVoiceDriven(): boolean {
        return !!this.#mediaAsset || !!this.#voiceoverAsset;
    }

    addVoiceoverAsset(id?: string) {
        if (this.#voiceoverAsset) {
            throw new Error('addVoiceoverAsset called while SceneGroup already has a voiceover');
        }
        if (this.#mediaAsset) {
            throw new Error('Scene group already has a media asset. Cannot add voiceover asset.');
        }
        const settings: {
            fetchResources: FetchResources;
            name: string;
            autoUpdate: boolean;
            parent: SceneGroup;
            id?: string;
            getAudioContext: () => AudioContext | OfflineAudioContext;
            getIsExternallyMuted: () => boolean;
        } = {
            fetchResources: this.fetchResources,
            name: 'SceneGroupVoiceover',
            autoUpdate: this.autoUpdate,
            parent: this,
            getAudioContext: () => this.root.audioContext,
            getIsExternallyMuted: () => this.root.muted,
            ...(id ? { id } : {}),
        };
        this.#voiceoverAsset = new AudioAsset(settings);

        // Register with Luminary asset index for O(1) lookups
        // AudioAsset is not part of the Asset union type but still needs registration
        this.root._registerAsset(this.#voiceoverAsset as unknown as Asset);

        this.requiresUpdate = true;

        return this.#voiceoverAsset;
    }

    removeVoiceoverAsset() {
        if (!this.#voiceoverAsset) {
            throw new Error('removeVoiceoverAsset called while SceneGroup has no voiceover');
        }

        this.#voiceoverAsset.destroy();
        this.#voiceoverAsset = null;
        this.requiresUpdate = true;
    }

    /**
     * The original transcript of the scene group. This is the transcript used by fancy captions.
     * For regular captions, see the transcript on the captions api instead.
     */
    get originalTranscript(): Transcript {
        return this.#originalTranscript;
    }

    /**
     * Run tasks in batch by setting autoUpdate and autoDraw to false temporarily.
     */
    #batchRun<T>(callback: () => T): T {
        return runInAction(() => {
            const initAutoDraw = this.autoDraw;
            const initAutoUpdate = this.autoUpdate;
            this.autoUpdate = false;
            this.autoDraw = false;

            try {
                return callback();
            } finally {
                this.autoUpdate = initAutoUpdate;
                this.autoDraw = initAutoDraw;
            }
        });
    }

    /**
     * Every fancy caption asset this group keeps in sync with `originalTranscript`, the captions
     * manager's own included: its playback regions resolve through `luminaryParent`, which forwards
     * from the manager to this group.
     */
    #fancyCaptionAssets() {
        return this.getAssetsByType(Config.FANCY_CAPTION_ASSET_TYPE);
    }

    #getLinkedMediaAssets() {
        const mediaAssets = this.#scenes.flatMap((s) => s.getAssetsByType(Config.MEDIA_ASSET_TYPE));
        return mediaAssets.filter((m) => m.linkToSceneGroup);
    }

    #getLinkedMediaAssetSource() {
        if (this.mediaAsset) {
            return this.mediaAsset;
        }
        if (this.avatar.foreground || this.avatar.background) {
            return this.avatar;
        }
    }

    firstScene() {
        return this.#scenes[0];
    }

    lastScene() {
        return this.#scenes[this.size - 1];
    }

    /**
     * @internal
     */
    _nextScene(sceneId: string): Scene | null {
        const size = this.size;
        const curIndex = this.#getSceneIndex(sceneId);
        const nextIndex = curIndex + 1;
        if (nextIndex < size) {
            return this.#scenes[nextIndex];
        }
        const nextSceneGroup = this.#sceneGroupManager._nextSceneGroup(this.id);
        if (!nextSceneGroup) {
            return null;
        }
        return nextSceneGroup.firstScene();
    }

    /**
     * @internal
     */
    _prevScene(sceneId: string): Scene | null {
        const curIndex = this.#getSceneIndex(sceneId);
        const prevIndex = curIndex - 1;
        if (prevIndex >= 0) {
            return this.#scenes[prevIndex];
        }
        const prevSceneGroup = this.#sceneGroupManager._prevSceneGroup(this.id);
        if (!prevSceneGroup) {
            return null;
        }
        return prevSceneGroup.lastScene();
    }

    get isOutroSceneGroup() {
        return this.#scenes.length === 1 && this.#scenes[0].sceneCategory === Config.CATEGORY_OUTRO;
    }

    get isBrandingSceneGroup() {
        return this.#scenes.length === 1 && this.#scenes[0].sceneCategory === Config.CATEGORY_BRAND;
    }

    /**
     * Find the index of this SceneGroup in luminary.sceneGroups.
     */
    get index(): number {
        return this.#sceneGroupManager._getSceneGroupIndex(this.id);
    }

    /**
     * The sceneGroup's global musicVolumeOverride override (if true it silences the background track for
     * the duration of the sceneGroup)
     * If null, it means the override is not set and the sceneGroup will use the global music volume.
     */
    get musicVolumeOverride(): number | null {
        return this.#musicVolumeOverride;
    }

    set musicVolumeOverride(value) {
        if (value === this.#musicVolumeOverride) {
            return;
        }
        assertNumberBetween(value as number, 0, 100, true);
        this.#musicVolumeOverride = value;
        this.requiresUpdate = true;
    }

    override set autoUpdate(value: boolean) {
        if (this.autoUpdate === value) {
            return;
        }

        assertBoolean(value);
        this.#originalTranscript.autoUpdate = value;
        this.#scenes.forEach((scene) => {
            scene.autoUpdate = value;
        });
        if (this.#voiceoverAsset) {
            this.#voiceoverAsset.autoUpdate = value;
        }
        if (this.#waveformAudioAsset) {
            this.#waveformAudioAsset.autoUpdate = value;
        }
        super.autoUpdate = value;
    }

    override get autoUpdate(): boolean {
        return super.autoUpdate;
    }

    /**
     * Deeply sets autoDraw on all BaseAssets within this SceneGroup.
     */
    set autoDraw(value: boolean) {
        if (this.#autoDraw === value) {
            return;
        }
        assertBoolean(value);
        this.#autoDraw = value;

        // Setting autoDraw causes assets to build, then load.
        // We need to load the SceneGroup MediaAsset first so that scenes which depend on
        // it are not stuck waiting for the SceneGroup MediaAsset, creating a deadlock in the AssetNetworkManager.
        if (this.#mediaAsset) {
            this.#mediaAsset.autoDraw = value;
        }
        this.avatar.autoDraw = value;
        this.#scenes.forEach((scene) => {
            scene.autoDraw = value;
        });
        this.#workspace.autoDraw = value;
    }

    get autoDraw(): boolean {
        return this.#autoDraw;
    }

    get currentTime() {
        return this.#timeline.currentTime;
    }

    /**
     * @internal
     */
    get _timeline() {
        return this.#timeline;
    }

    /**
     * @internal
     */
    async _propagateBuffering(bufferingMode: BufferingMode): Promise<void> {
        try {
            await this.#propagateBuffering(bufferingMode);
        } catch (e) {
            if (e instanceof BufferingAbortedError) {
                // Don't propagate an error, but rather just resolve
                return;
            }
            throw e;
        }
    }

    get isPlaying() {
        return this._timeline.playing() && this._timeline.active;
    }

    #setupOnStart() {
        this._timeline.on('start', () => this._propagatePlay());
    }

    #setupOnEnd() {
        this._timeline.on('end', () => this._propagatePause());
    }

    /**
     * @internal
     */
    _propagatePlay() {
        if (this.#mediaAsset) {
            this.#mediaAsset._propagatePlay();
        }
        if (this.#voiceoverAsset) {
            this.#voiceoverAsset._propagatePlay();
        }
        this.avatar._propagatePlay();
        this.#assetsWorkspace.getAssets().forEach((child) => {
            child._propagatePlay();
        });
        this.getActiveScenesAt(this.currentTime).forEach((scene) => {
            scene._propagatePlay();
        });
    }

    /**
     * @internal
     */
    _propagateTextDirection(textDirection: TextDirection) {
        this.captions._propagateTextDirection(textDirection);

        this.#scenes.forEach((scene) => {
            scene.textDirection = textDirection;
        });
    }

    protected override _getLifecycleAwareObjects(): LifecycleAware[] {
        const objects: LifecycleAware[] = [this.#assetsWorkspace, ...this.#scenes, this.avatar];
        if (this.#mediaAsset) {
            objects.push(this.#mediaAsset);
        }
        if (this.#voiceoverAsset) {
            objects.push(this.#voiceoverAsset);
        }
        return objects.filter((obj) => obj && !obj.destroyed);
    }

    /**
     * The waveform audio is deliberately absent from the lifecycle-aware objects: it feeds
     * `isLoaded`, and a dead `audio_url` would then reject `onAssetsLoaded()` and fail the whole
     * render over a decorative waveform. `MediaAsset` draws the same line for its own audio asset.
     */
    override get canPlay(): boolean {
        if (this.#waveformAudioAsset && !this.#waveformAudioAsset.canPlay) {
            return false;
        }
        return super.canPlay;
    }

    /**
     * @internal
     */
    _emitAssetsCanPlay() {
        if (this.root) {
            this.root.emit('assetsCanPlay');
        }
    }

    seek(seekMode: SeekMode) {
        const timeChangedSinceLastAnimate = this.currentTime !== this.#lastAnimatedTime;
        const shouldAnimate =
            timeChangedSinceLastAnimate ||
            this.#layoutChangedSinceLastAnimate ||
            this.#assetsChangedSinceLastAnimate;

        if (shouldAnimate) {
            if (this.#mediaAsset) {
                this.#mediaAsset.seek(this._timeline.progress, seekMode);
            }
            this.avatar.seek(this._timeline.progress, seekMode);
            this.#assetsWorkspace.seek(this._timeline.progress, seekMode);
            this.#drawHelpers();

            // animate the scenes separately because their assets are relative to the scene's timeline
            const activeScenes = this.getActiveScenesAt(this.currentTime);

            activeScenes.forEach((scene) => {
                scene.seek({ children: shouldAnimate, transition: true }, seekMode);
            });
        }

        this.#lastAnimatedTime = this.currentTime;
        this.#layoutChangedSinceLastAnimate = false;
        this.#assetsChangedSinceLastAnimate = false;
    }

    async #propagateBuffering(bufferingMode: BufferingMode): Promise<void> {
        const activeScenes = this.getActiveScenesAt(this.currentTime);

        if (bufferingMode === BufferingMode.PrepareForPlayback) {
            // Seek all subsequent slides to 0. This is required to make sure following scenes'
            // time aware assets (like videos) will start at the right time.
            const startIndex = activeScenes.length
                ? this.#getSceneIndex(activeScenes[activeScenes.length - 1].id) + 1
                : 0;
            for (let i = startIndex; i < this.#scenes.length; i++) {
                // Do not wait for buffering to resolve.
                // Scenes can continue buffering while we start playing.
                const sceneId = this.#scenes[i].id;
                const scene = this.#sceneGroupManager._getSceneById(sceneId)!;
                scene._propagateBuffering(BufferingMode.PrepareForPlayback).catch((e) => {
                    if (!scene.destroyed) {
                        if (!(e instanceof Error)) {
                            ErrorReporter.capture('received non-error', {
                                level: 'error',
                                extra: {
                                    originalErrorObject: e,
                                },
                            });
                        } else {
                            ErrorReporter.capture(e, {
                                extra: {
                                    action: 'seek future scenes',
                                },
                            });
                        }
                    }
                });
            }

            // If this scene group is in the future then prepare the assets on the scene group
            if (this.startTime > this.root.currentTime) {
                if (this.#mediaAsset) {
                    // This is preparing for the future so don't wait for media asset to buffer
                    void this.#mediaAsset._bufferAtTime(0, bufferingMode);
                }
                if (this.#voiceoverAsset) {
                    void this.#voiceoverAsset._bufferAtTime(0, bufferingMode);
                }
                void this.avatar._bufferAtTime(0, bufferingMode);
            }

            // prepare for playback is just preparing upcoming scenes for playback, so we don't
            // need to buffer any of the active scenes, since they will be buffered separately
            return;
        }

        return new Promise<void>((resolve, reject) => {
            this.#abortBuffering = reject;
            const sceneGroupMediaBufferPromise = this.#mediaAsset
                ? this.#mediaAsset._bufferAtTime(this.currentTime, bufferingMode)
                : Promise.resolve();
            const sceneGroupAudioBufferPromise = this.#voiceoverAsset
                ? this.#voiceoverAsset._bufferAtTime(this.currentTime, bufferingMode)
                : Promise.resolve();
            Promise.all([
                this.#assetsWorkspace._bufferAtTime(this.currentTime, bufferingMode),
                this.avatar._bufferAtTime(this.currentTime, bufferingMode),
                sceneGroupMediaBufferPromise,
                sceneGroupAudioBufferPromise,
                ...activeScenes.map(async (scene) => scene._propagateBuffering(bufferingMode)),
            ])
                .then((_) => resolve())
                .catch(reject);
        });
    }

    /**
     * Get the active scenes at the time for this scene group.
     */
    getActiveScenesAt(currentTime: number): Scene[] {
        return this.#timeline
            .getActiveChildrenIndicesAt(currentTime)
            .map((index) => this.#scenes[index]);
    }

    /**
     * Remove a scene by ID.
     */
    removeScene(sceneId: string) {
        const scene = this.#scenesById.get(sceneId);
        if (!scene) {
            return;
        }
        // remove any render tasks submitted by the scene we're about to delete
        this.root._renderSceneQueue.dequeueTasksForScene(sceneId);

        runInAction(() => {
            const index = this.#scenes.indexOf(scene);
            this.#scenes.splice(index, 1);
            scene.destroy(true);
            this.#scenesById.set(sceneId, null as unknown as Scene);
            this.#scenesById.delete(sceneId);
        });
        this.requiresUpdate = true;
    }

    /** @internal */
    _walkLogicalStartTime(sceneId: string): number {
        const sceneIndex = this.#getSceneIndex(sceneId);
        if (sceneIndex === -1) {
            throw new Error(`Scene ${sceneId} is not in this scene group`);
        }
        return this.#scenes
            .slice(0, sceneIndex)
            .reduce((startTime, scene) => startTime + scene.getLogicalDuration(), this.startTime);
    }

    /**
     * Apply per-scene "logical" durations (durations that do not include the transition
     * into the scene), so that each scene reports back the logical duration it was given.
     *
     * Scenes are processed in scene order so that each scene's transition cap is computed
     * against the previous scene's already-adjusted duration.
     */
    setLogicalDurations(entries: Array<{ sceneId: string; logicalDuration: number }>): void {
        const logicalById = new Map(entries.map((e) => [e.sceneId, e.logicalDuration]));
        for (const scene of this.#scenes) {
            const logical = logicalById.get(scene.id);
            if (logical === undefined) {
                continue;
            }
            scene.duration = logical;
            const transition = scene._calculateTransitionDurationWithinSceneGroup();
            scene.duration = logical + transition;
        }
    }

    /**
     * Given a scene id, move the scene to a new index within the group.
     *
     * The move is a removal followed by an insertion, so it runs as an action: a derivation reading the scenes would
     * otherwise wake between the two and see the group without the scene at all.
     */
    moveSceneToIndex(sceneId: string, newIndex: number) {
        if (newIndex >= this.size) {
            throw Error(
                `Unable to move scene to index ${newIndex}. SceneGroup only has ${this.size} scenes.`,
            );
        }
        const oldIndex = this.#getSceneIndex(sceneId);
        const scene = this.#scenes[oldIndex];

        runInAction(() => {
            this.#scenes.splice(oldIndex, 1);
            this.#scenes.splice(newIndex, 0, scene);
        });

        this.#timeline.moveChild(oldIndex, newIndex);
        this.requiresUpdate = true;
    }

    /**
     * List of all scenes in group. Returns a copy, so a caller cannot reach into the group and mobx observable
     * arrays never cross the public API.
     */
    get scenes(): readonly Scene[] {
        return this.#scenes.slice();
    }

    /**
     * The live scenes array. Internal callers use this to avoid paying for the copy that
     * [scenes]{@link SceneGroup#scenes} makes; it must never be handed outside luminary.
     *
     * @internal
     */
    get _scenes(): Scene[] {
        return this.#scenes;
    }

    /**
     * Internal Map for O(1) scene lookups by ID.
     * @internal
     */
    get _scenesById(): Map<string, Scene> {
        return this.#scenesById;
    }

    /**
     * Get size of SceneGroup (number of scenes contained in group).
     */
    get size(): number {
        return this.#scenes.length;
    }

    /**
     * The scene group's duration in milliseconds
     */
    get duration(): number {
        return this._timeline.duration;
    }

    /**
     * The start time of the scene group relative to its parent (luminary)
     */
    get startTime(): number {
        return this._timeline.startTimeRelativeToAncestor(this.#globalTimeline);
    }

    /**
     * The end time of the scene group relative to its parent (luminary)
     */
    get endTime(): number {
        return this.startTime + this.duration;
    }

    /**
     * Get the index of a scene in the group.
     */
    #getSceneIndex(sceneId: string) {
        return this.#scenes.findIndex((scene) => scene.id === sceneId);
    }

    /**
     * @internal
     */
    _updateForRender() {
        if (this.mediaAsset) {
            this.mediaAsset.updateForRender();
        }
        this.avatar.updateForRender();
        this.captions.updateForRender();
    }

    /**
     * @internal
     */
    get _workspace() {
        return this.#workspace;
    }

    override get slide(): Scene | null {
        // A SceneGroup is not associated with just a single slide
        return null;
    }

    override get parent() {
        return this.#sceneGroupManager;
    }

    /**
     * The grading values a linked media asset mirrors, in a shape a reaction can compare. Returns
     * null when there is nothing to mirror from.
     */
    static #gradingSnapshot(parentMediaAsset: LinkedMediaSource | undefined) {
        if (!parentMediaAsset) {
            return null;
        }
        if (Config.isCompositingAsset(parentMediaAsset)) {
            return { brightness: parentMediaAsset.brightness };
        }
        if (!Config.isMediaAsset(parentMediaAsset)) {
            return null;
        }
        return {
            brightness: parentMediaAsset.brightness,
            contrast: parentMediaAsset.contrast,
            hue: parentMediaAsset.hue,
            saturation: parentMediaAsset.saturation,
            lightness: parentMediaAsset.lightness,
            blur: parentMediaAsset.blur,
            vignetting: parentMediaAsset.vignetting,
            vignettingAlpha: parentMediaAsset.vignettingAlpha,
            vignettingBlur: parentMediaAsset.vignettingBlur,
            noise: parentMediaAsset.noise,
            noiseSize: parentMediaAsset.noiseSize,
            enableGreyscale: parentMediaAsset.enableGreyscale,
            gradingOverrideSource: parentMediaAsset.gradingOverride?.source ?? null,
            gradingOverrideIntensity: parentMediaAsset.gradingOverride?.intensity ?? null,
        };
    }

    static #applyLinkedMediaGrading(
        parentMediaAsset: LinkedMediaSource | undefined,
        linkedAssets: LinkedMediaAsset[],
    ) {
        if (!parentMediaAsset) {
            return;
        }
        if (Config.isCompositingAsset(parentMediaAsset)) {
            linkedAssets.forEach((media) => {
                media.brightness = parentMediaAsset.brightness;
            });
        } else if (Config.isMediaAsset(parentMediaAsset)) {
            linkedAssets.forEach((media) => {
                media.brightness = parentMediaAsset.brightness;
                media.contrast = parentMediaAsset.contrast;
                media.hue = parentMediaAsset.hue;
                media.saturation = parentMediaAsset.saturation;
                media.lightness = parentMediaAsset.lightness;
                media.blur = parentMediaAsset.blur;
                media.vignetting = parentMediaAsset.vignetting;
                media.vignettingAlpha = parentMediaAsset.vignettingAlpha;
                media.vignettingBlur = parentMediaAsset.vignettingBlur;
                media.noise = parentMediaAsset.noise;
                media.noiseSize = parentMediaAsset.noiseSize;
                media.enableGreyscale = parentMediaAsset.enableGreyscale;
                if (parentMediaAsset.gradingOverride) {
                    if (!media.gradingOverride) {
                        media.addGradingOverride();
                    }
                    media.gradingOverride!.source = parentMediaAsset.gradingOverride.source;
                    media.gradingOverride!.intensity = parentMediaAsset.gradingOverride.intensity;
                } else if (media.gradingOverride) {
                    media.removeGradingOverride();
                }
            });
        }
    }

    /**
     * Clears the avatar's rendered output when the voiceover it was generated from changes. A first url is not a
     * change, so there is nothing rendered from an earlier voiceover to discard.
     */
    static #clearAvatarRenderUrlOnVoiceoverChange(
        foreground: TimeAwareMediaAsset | null,
        currentMp3Url: string | null,
        previousMp3Url: string | null,
    ): void {
        if (currentMp3Url === null || currentMp3Url === previousMp3Url) {
            return;
        }

        if (previousMp3Url && foreground?.sourceData) {
            foreground.sourceData = {
                ...foreground.sourceData,
                render_url: null,
            };
        }
    }

    override get readonlyProperties() {
        return ['id'];
    }

    /**
     * @internal
     */
    _mediaAssetCustomLoad(value: Record<string, unknown> | null) {
        const initAutoUpdate = this.autoUpdate;
        this.autoUpdate = false;

        if (!value) {
            return;
        }
        this.addMediaAsset(value.id as string);
        this.mediaAsset!.load(value);

        this.autoUpdate = initAutoUpdate;
    }

    /**
     * @internal
     */
    _mediaAssetCustomSave() {
        return this.mediaAsset?.save() ?? null;
    }

    /**
     * @internal
     */
    _voiceoverAssetCustomLoad(value: Record<string, unknown> | null) {
        if (!value) {
            return;
        }
        const initAutoUpdate = this.autoUpdate;
        this.autoUpdate = false;

        this.addVoiceoverAsset(value.id as string);
        this.#voiceoverAsset!.load(value);

        this.autoUpdate = initAutoUpdate;
    }

    /**
     * One action for the whole clear. Each step notifies on its own -- dropping the media asset, emptying the
     * transcript -- but a sequence of them is not atomic, so without this a derivation runs once per step against a
     * group that is only partly cleared.
     */
    override clear() {
        runInAction(() => {
            super.clear();

            if (this.#mediaAsset) {
                this.removeMediaAsset();
            }
            if (this.#voiceoverAsset) {
                this.removeVoiceoverAsset();
            }
            this.originalTranscript.clear();
            this.captions.clear();
        });
    }

    override get propertiesToSerialize() {
        return ['captions', 'originalTranscript', 'mediaAsset', 'voiceoverAsset', 'avatar'];
    }

    override get editableProperties() {
        return super.editableProperties.concat(['musicVolumeOverride']);
    }

    /**
     * One action for the whole group. Every step here is atomic on its own — `super.destroy`, each scene's `destroy`,
     * emptying `#scenes` — but a sequence of them is not, so without this a derivation runs against a partly torn
     * down group once per step.
     */
    override destroy(options?: DestroyOptions) {
        runInAction(() => {
            super.destroy(options);
            this.#abortBuffering(
                new BufferingAbortedError(this.classType, this.index, 'destroy called'),
            );
            SceneGroup.#removeHelpers(this.#helperScene);

            this.#scenes.forEach((scene) => scene.destroy(options));
            if (this.#mediaAsset) {
                this.#mediaAsset.destroy(options);
            }
            if (this.#voiceoverAsset) {
                this.#voiceoverAsset.destroy();
            }
            this.#waveformAudioAsset?.destroy();
            this.#waveformAudioAsset = null;
            this.avatar.destroy();
            this._timeline.destroy();
            this.#scenes = [];
            this.#scenesById = new Map<string, Scene>();
            this.#workspace.destroy(options);
        });
    }
}
